import React, { useState, lazy } from 'react';
import { Home, Users, Globe, Briefcase, MessageSquare, Plus, Search, Filter, X, ChevronDown, Leaf, Zap, Award, Clock, BarChart2, Lightbulb, AlertCircle, CheckCircle, Settings, TrendingUp, BarChart, Target, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function CollaborationPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('proposals');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    field: '',
    network: false
  });
  const [activeTab, setActiveTab] = useState('tutte');
  const [expandedProject, setExpandedProject] = useState<number | null>(null);
  // Common filter categories based on skills in the data
  const filterCategories = ['React', 'Frontend', 'UX/UI', 'Mobile', 'Python', 'Data Analysis', 'Design', 'Marketing', 'Open Source', 'JavaScript', 'Sustainability', 'Green Tech'];
  const countries = ['Italy', 'France', 'Germany', 'Spain', 'United Kingdom', 'United States'];
  const fields = ['Technology', 'Design', 'Marketing', 'Business', 'Education', 'Health', 'Finance', 'Sustainability'];
  const toggleFilter = (filter: string) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };
  const clearFilters = () => {
    setActiveFilters([]);
    setSearchQuery('');
    setFilters({
      country: '',
      field: '',
      network: false
    });
  };
  const handleFilterChange = (key: string, value: any) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };
  const closeFiltersModal = () => {
    setShowFiltersModal(false);
  };
  const toggleProjectExpansion = (id: number) => {
    if (expandedProject === id) {
      setExpandedProject(null);
    } else {
      setExpandedProject(id);
    }
  };
  // Sample collaboration proposals data - simplified
  const collaborationProposals = [{
    id: 1,
    title: 'React Developer for e-commerce project',
    description: 'Looking for a React developer with e-commerce experience to collaborate on a 3-month project. Offering competitive compensation and possibility of future partnership.',
    skills: ['React', 'JavaScript', 'Frontend', 'E-commerce'],
    status: 'open',
    isOwner: false,
    hasSustainabilityPotential: false,
    author: {
      name: 'Marco Rossi',
      role: 'Product Manager',
      image: 'https://randomuser.me/api/portraits/men/32.jpg'
    },
    postedAt: '2 days ago'
  }, {
    id: 2,
    title: 'UX/UI Designer needed for mobile app',
    description: 'Growing startup looking for UX/UI designer to improve our mobile app interface. Experience with Figma and design systems required.',
    skills: ['UX/UI', 'Design', 'Mobile', 'Figma'],
    status: 'accepted',
    isOwner: false,
    hasSustainabilityPotential: false,
    author: {
      name: 'Laura Bianchi',
      role: 'CTO',
      image: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    postedAt: '1 week ago'
  }, {
    id: 3,
    title: 'Data Analyst for research project',
    description: 'Seeking data analyst with Python skills and data visualization experience to collaborate on a research project in the healthcare sector.',
    skills: ['Python', 'Data Analysis', 'Visualization', 'Healthcare'],
    status: 'pending',
    isOwner: false,
    hasSustainabilityPotential: true,
    author: {
      name: 'Giovanni Verdi',
      role: 'Research Lead',
      image: 'https://randomuser.me/api/portraits/men/67.jpg'
    },
    postedAt: '3 days ago'
  }];
  // Sample active collaborations data
  const activeCollaborations = [{
    id: 101,
    title: 'App for reducing food waste',
    description: 'We are developing an app that connects restaurants and supermarkets with consumers to reduce food waste.',
    progress: 65,
    startDate: '15/03/2023',
    endDate: '30/08/2023',
    team: [{
      name: 'Sofia Ricci',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Marco Bianchi',
      role: 'React Developer',
      image: 'https://randomuser.me/api/portraits/men/32.jpg'
    }],
    hasSustainabilityPotential: true,
    sustainabilityScore: 78,
    sustainabilityMetrics: {
      carbonReduction: 65,
      resourceEfficiency: 82,
      socialImpact: 90
    },
    sustainabilityTips: ['Consider using servers powered by renewable energy', 'Implement features to calculate CO2 saved', 'Add partnerships with environmental organizations'],
    tags: ['Mobile', 'React Native', 'Sustainability', 'Food Tech']
  }, {
    id: 102,
    title: 'E-learning platform for developers',
    description: 'We are creating an e-learning platform specialized in advanced programming courses.',
    progress: 40,
    startDate: '05/04/2023',
    endDate: '15/10/2023',
    team: [{
      name: 'Sofia Ricci',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Andrea Russo',
      role: 'Full-stack Developer',
      image: 'https://randomuser.me/api/portraits/men/67.jpg'
    }],
    hasSustainabilityPotential: false,
    tags: ['Web', 'React', 'Node.js', 'Education']
  }, {
    id: 103,
    title: 'Energy monitoring system for buildings',
    description: 'Project to develop an IoT system that monitors and optimizes energy consumption in buildings.',
    progress: 85,
    startDate: '10/01/2023',
    endDate: '25/07/2023',
    team: [{
      name: 'Sofia Ricci',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/woFngADa1aU2Q5MqNXmffj/pexels-olly-774909.jpg'
    }, {
      name: 'Paolo Ferrari',
      role: 'IoT Specialist',
      image: 'https://randomuser.me/api/portraits/men/22.jpg'
    }],
    hasSustainabilityPotential: true,
    sustainabilityScore: 92,
    sustainabilityMetrics: {
      carbonReduction: 95,
      resourceEfficiency: 90,
      socialImpact: 75
    },
    sustainabilityTips: ['Add ML-based consumption prediction features', 'Implement gamification to encourage energy saving', 'Consider integration with local smart grids'],
    tags: ['IoT', 'Energy', 'Sustainability', 'Data Visualization']
  }];
  // Sample impact data
  const impactSuggestions = [{
    id: 1,
    projectId: 102,
    projectName: 'E-learning platform for developers',
    title: 'Implement a carbon footprint calculator',
    description: 'Add a calculator that shows how much CO2 is saved by using your e-learning platform compared to traditional training.',
    difficulty: 'medium',
    impact: 'high',
    estimatedTimeToImplement: '1-2 weeks',
    icon: <BarChart className="w-5 h-5 text-green-600" data-id="element-47" />
  }, {
    id: 2,
    projectId: 102,
    projectName: 'E-learning platform for developers',
    title: 'Add content on sustainable programming',
    description: 'Create a dedicated section for best practices on writing energy-efficient code and developing eco-friendly applications.',
    difficulty: 'low',
    impact: 'medium',
    estimatedTimeToImplement: '3-5 days',
    icon: <Lightbulb className="w-5 h-5 text-yellow-500" data-id="element-48" />
  }, {
    id: 3,
    projectId: 102,
    projectName: 'E-learning platform for developers',
    title: 'Optimize cloud infrastructure',
    description: 'Migrate to cloud providers that use renewable energy and implement automatic scaling strategies to reduce energy consumption.',
    difficulty: 'high',
    impact: 'very high',
    estimatedTimeToImplement: '3-4 weeks',
    icon: <Zap className="w-5 h-5 text-purple-600" data-id="element-49" />
  }];
  // Sample impact board
  const impactBoard = [{
    id: 1,
    title: 'Community sustainable initiatives',
    description: 'Projects and initiatives started by our community members to promote environmental sustainability.',
    posts: [{
      id: 101,
      author: {
        name: 'Marco Verdi',
        image: 'https://randomuser.me/api/portraits/men/42.jpg'
      },
      content: 'I just implemented an energy monitoring system in my project that reduced consumption by 35%. Happy to share how I did it!',
      likes: 24,
      comments: 5,
      date: '2 days ago'
    }, {
      id: 102,
      author: {
        name: 'Laura Bianchi',
        image: 'https://randomuser.me/api/portraits/women/43.jpg'
      },
      content: 'Looking for collaborators for an app project that calculates and offsets personal carbon footprint. Anyone interested?',
      likes: 18,
      comments: 12,
      date: '5 days ago'
    }]
  }, {
    id: 2,
    title: 'Resources for sustainable development',
    description: 'Share and discover useful resources for implementing sustainable development practices in your projects.',
    posts: [{
      id: 201,
      author: {
        name: 'Giulia Rossi',
        image: 'https://randomuser.me/api/portraits/women/22.jpg'
      },
      content: 'I found this fantastic guide on how to optimize images to reduce data consumption: https://example.com/guide',
      likes: 31,
      comments: 7,
      date: '1 week ago'
    }]
  }];
  // Filter posts based on search query and active filters
  const filteredProposals = collaborationProposals.filter(post => {
    // Search in title and description
    const matchesSearch = searchQuery === '' || post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.description.toLowerCase().includes(searchQuery.toLowerCase());
    // Check if post matches active filters
    const matchesFilters = activeFilters.length === 0 || post.skills.some(skill => activeFilters.includes(skill));
    // Check if post matches the active tab
    let matchesTab = true;
    if (activeTab === 'accettate') {
      matchesTab = post.status === 'accepted';
    } else if (activeTab === 'in-attesa') {
      matchesTab = post.status === 'pending';
    } else if (activeTab === 'mie') {
      matchesTab = post.isOwner;
    }
    return matchesSearch && matchesFilters && matchesTab;
  });
  return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans" data-id="element-50">
      {/* Main content */}
      <div className="flex-grow bg-white pt-6 pb-20 md:pb-10" data-id="element-51">
        <div className="max-w-6xl mx-auto px-0" data-id="element-52">
          {/* Title row */}
          <div className="flex justify-between items-center mb-6" data-id="element-53">
            <h1 className="text-2xl md:text-3xl font-light font-sharp-grotesk" data-id="element-54">
              Collaboration
            </h1>
            <button className="px-4 py-2 bg-black text-white rounded-lg text-sm flex items-center" data-id="element-55">
              <Plus className="w-4 h-4 mr-2" data-id="element-56" />
              New Project
            </button>
          </div>
          {/* Subtitle and description */}
          <div className="mb-4" data-id="element-57">
            <p className="text-gray-600" data-id="element-58">
              Find collaboration opportunities, manage your projects and
              discover how to improve sustainability impact.
            </p>
          </div>
          {/* Proposals Section - Simplified */}
          {activeSection === 'proposals' && <>
              {/* Sub-tabs for proposals */}
              <div className="mb-6" data-id="element-59">
                <div className="w-full md:w-auto overflow-x-auto" data-id="element-60">
                  <div className="border-b border-gray-200 mb-4" data-id="element-61">
                    <div className="flex -mb-px" data-id="element-62">
                      <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'tutte' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('tutte')} data-id="element-63">
                        All
                      </button>
                      <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'accettate' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('accettate')} data-id="element-64">
                        Accepted
                      </button>
                      <button className={`mr-4 py-2 px-1 text-sm font-medium border-b-2 ${activeTab === 'mie' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('mie')} data-id="element-65">
                        My Proposals
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Search bar */}
              <div className="mb-6" data-id="element-66">
                <div className="flex items-center gap-3" data-id="element-67">
                  <div className="relative flex-grow" data-id="element-68">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-69">
                      <Search className="w-4 h-4 text-gray-400" data-id="element-70" />
                    </div>
                    <input type="text" className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm" placeholder="Search by title, description or skills..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-71" />
                  </div>
                  <button className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg" onClick={() => setShowFiltersModal(true)} data-id="element-72">
                    <Filter className="h-5 w-5 mr-2" data-id="element-73" />
                    <span data-id="element-74">Filters</span>
                  </button>
                </div>
              </div>
              {/* Collaboration board content - Simplified */}
              <div className="space-y-3" data-id="element-75">
                {filteredProposals.length > 0 ? filteredProposals.map(post => <div key={post.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-76">
                      {/* Header */}
                      <div className="border-b border-gray-100 px-4 py-3" data-id="element-77">
                        <h3 className="font-medium text-base font-sharp-grotesk mb-2" data-id="element-78">
                          {post.title}
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-79">
                          {post.hasSustainabilityPotential && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full flex items-center" data-id="element-80">
                              <Leaf className="w-3 h-3 mr-1" data-id="element-81" />
                              Eco-potential
                            </span>}
                          {post.status === 'accepted' && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full" data-id="element-82">
                              Accepted
                            </span>}
                        </div>
                      </div>
                      {/* Content */}
                      <div className="p-4" data-id="element-83">
                        <div className="mb-4" data-id="element-84">
                          <div className="flex items-start gap-3 mb-3" data-id="element-85">
                            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" data-id="element-86">
                              <img src={post.author.image} alt={post.author.name} className="w-full h-full object-cover" data-id="element-87" />
                            </div>
                            <div className="flex-1" data-id="element-88">
                              <div className="flex items-center text-xs text-gray-500" data-id="element-89">
                                <span className="font-medium text-gray-700 mr-1" data-id="element-90">
                                  {post.author.name}
                                </span>
                                <span className="text-gray-400" data-id="element-91">
                                  • {post.author.role} • {post.postedAt}
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3 font-rubik" data-id="element-92">
                            {post.description}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-2" data-id="element-93">
                            {post.skills.map((skill, index) => <span key={index} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-id="element-94">
                                {skill}
                              </span>)}
                          </div>
                        </div>
                      </div>
                      {/* Footer */}
                      <div className="border-t border-gray-100 pt-3 px-4 pb-4" data-id="element-95">
                        <div className="flex items-center justify-between" data-id="element-96">
                          <div className="flex items-center text-gray-500 text-sm" data-id="element-97">
                            <span data-id="element-98">
                              {post.status === 'pending' ? 'Pending response' : post.status === 'accepted' ? 'Active collaboration' : 'Open proposal'}
                            </span>
                          </div>
                          <div className="flex gap-2" data-id="element-99">
                            <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg flex items-center" data-id="element-100">
                              <MessageSquare className="w-3 h-3 mr-1" data-id="element-101" />
                              Contact
                            </button>
                            <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg" data-id="element-102">
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>) : <div className="text-center py-8" data-id="element-103">
                    <p className="text-gray-500 font-rubik" data-id="element-104">
                      No results found. Try changing your filters.
                    </p>
                  </div>}
              </div>
            </>}
          {/* Active Collaborations Section - Simplified */}
          {activeSection === 'active' && <div className="space-y-6" data-id="element-105">
              <div className="flex items-center justify-between mb-2" data-id="element-106">
                <h2 className="text-lg font-medium" data-id="element-107">Your active projects</h2>
              </div>
              {activeCollaborations.length > 0 ? activeCollaborations.map(project => <div key={project.id} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm" data-id="element-108">
                    <div className="flex flex-col space-y-4" data-id="element-109">
                      {/* Header with title and progress */}
                      <div className="flex justify-between items-start" data-id="element-110">
                        <div data-id="element-111">
                          <div className="flex items-center gap-2" data-id="element-112">
                            <h3 className="font-medium text-lg" data-id="element-113">
                              {project.title}
                            </h3>
                            {project.hasSustainabilityPotential && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full flex items-center" data-id="element-114">
                                <Leaf className="w-3 h-3 mr-1" data-id="element-115" />
                                Eco-potential
                              </span>}
                          </div>
                          <div className="text-sm text-gray-500 mt-1" data-id="element-116">
                            {project.startDate} - {project.endDate}
                          </div>
                        </div>
                        <div className="flex flex-col items-end" data-id="element-117">
                          <div className="text-sm font-medium mb-1" data-id="element-118">
                            {project.progress}% completed
                          </div>
                          <div className="w-24 bg-gray-200 rounded-full h-1.5" data-id="element-119">
                            <div className="bg-black h-1.5 rounded-full" style={{
                      width: `${project.progress}%`
                    }} data-id="element-120"></div>
                          </div>
                        </div>
                      </div>
                      {/* Description */}
                      <p className="text-sm text-gray-600" data-id="element-121">
                        {project.description}
                      </p>
                      {/* Team members */}
                      <div data-id="element-122">
                        <div className="text-sm font-medium mb-2" data-id="element-123">Team</div>
                        <div className="flex items-center gap-2" data-id="element-124">
                          {project.team.map((member, index) => <div key={index} className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-full" data-id="element-125">
                              <div className="w-5 h-5 rounded-full overflow-hidden" data-id="element-126">
                                <img src={member.image} alt={member.name} className="w-full h-full object-cover" data-id="element-127" />
                              </div>
                              <span className="text-xs" data-id="element-128">{member.name}</span>
                            </div>)}
                          <button className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-129">
                            <Plus className="w-3 h-3" data-id="element-130" />
                          </button>
                        </div>
                      </div>
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5" data-id="element-131">
                        {project.tags.map((tag, index) => <span key={index} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-132">
                            {tag}
                          </span>)}
                      </div>
                      {/* Action buttons */}
                      <div className="flex justify-between items-center" data-id="element-133">
                        <div className="flex gap-2" data-id="element-134">
                          <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg flex items-center" data-id="element-135">
                            <BarChart2 className="w-3 h-3 mr-1" data-id="element-136" />
                            Dashboard
                          </button>
                          <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg flex items-center" data-id="element-137">
                            <MessageSquare className="w-3 h-3 mr-1" data-id="element-138" />
                            Chat
                          </button>
                        </div>
                        <button className="text-xs text-gray-500 flex items-center" data-id="element-139">
                          <Settings className="w-3 h-3 mr-1" data-id="element-140" />
                          Settings
                        </button>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-id="element-141">
                  <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-142" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2" data-id="element-143">
                    No active projects
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-6" data-id="element-144">
                    You don't have any active collaborations yet. Start a new
                    project or join an existing proposal.
                  </p>
                  <button className="px-4 py-2 bg-black text-white rounded-lg text-sm flex items-center mx-auto" data-id="element-145">
                    <Plus className="w-4 h-4 mr-2" data-id="element-146" />
                    New Project
                  </button>
                </div>}
            </div>}
        </div>
      </div>
      {/* Filters Modal */}
      {showFiltersModal && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-147">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={closeFiltersModal} data-id="element-148"></div>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden transform mx-4" data-id="element-149">
            <div className="p-6" data-id="element-150">
              <div className="flex justify-between items-center mb-4" data-id="element-151">
                <h3 className="font-medium text-lg" data-id="element-152">Filter results</h3>
                <div className="flex items-center gap-3" data-id="element-153">
                  <button className="text-sm text-gray-500" onClick={clearFilters} data-id="element-154">
                    Reset filters
                  </button>
                  <button className="text-gray-500" onClick={closeFiltersModal} data-id="element-155">
                    <X className="h-5 w-5" data-id="element-156" />
                  </button>
                </div>
              </div>
              <div className="space-y-4" data-id="element-157">
                {/* Country filter */}
                <div data-id="element-158">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-159">
                    Country
                  </label>
                  <div className="relative" data-id="element-160">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.country} onChange={e => handleFilterChange('country', e.target.value)} data-id="element-161">
                      <option value="" data-id="element-162">All countries</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-163">
                          {country}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-164">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-165" />
                    </div>
                  </div>
                </div>
                {/* Field filter */}
                <div data-id="element-166">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-167">
                    Field
                  </label>
                  <div className="relative" data-id="element-168">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.field} onChange={e => handleFilterChange('field', e.target.value)} data-id="element-169">
                      <option value="" data-id="element-170">All fields</option>
                      {fields.map(field => <option key={field} value={field} data-id="element-171">
                          {field}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-172">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-173" />
                    </div>
                  </div>
                </div>
                {/* Sustainability filter */}
                <div className="flex items-center" data-id="element-174">
                  <input type="checkbox" id="sustainability-filter" checked={activeFilters.includes('Sustainability')} onChange={() => toggleFilter('Sustainability')} className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded" data-id="element-175" />
                  <label htmlFor="sustainability-filter" className="ml-2 block text-sm text-gray-700" data-id="element-176">
                    Only projects with sustainability potential
                  </label>
                </div>
                {/* Skills filter */}
                <div data-id="element-177">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-178">
                    Skills
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-179">
                    {filterCategories.map(category => <button key={category} className={`px-3 py-1.5 text-sm rounded-full ${activeFilters.includes(category) ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`} onClick={() => toggleFilter(category)} data-id="element-180">
                        {category}
                      </button>)}
                  </div>
                </div>
                <button className="w-full mt-4 bg-black text-white py-2.5 rounded-lg" onClick={closeFiltersModal} data-id="element-181">
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 z-50" data-id="element-182">
        <button onClick={() => navigate('/')} className="flex flex-col items-center" data-id="element-183">
          <Home className="w-5 h-5 text-gray-400" data-id="element-184" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-185">Home</span>
        </button>
        <button onClick={() => navigate('/connections')} className="flex flex-col items-center" data-id="element-186">
          <Users className="w-5 h-5 text-gray-400" data-id="element-187" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-188">Connections</span>
        </button>
        <button onClick={() => navigate('/networking')} className="flex flex-col items-center" data-id="element-189">
          <Globe className="w-5 h-5 text-gray-400" data-id="element-190" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-191">Networking</span>
        </button>
        <button className="flex flex-col items-center" data-id="element-192">
          <Briefcase className="w-5 h-5 text-black" data-id="element-193" />
          <span className="text-xs mt-1 text-black" data-id="element-194">Collaboration</span>
        </button>
        <button onClick={() => navigate('/impact')} className="flex flex-col items-center" data-id="element-195">
          <Leaf className="w-5 h-5 text-gray-400" data-id="element-196" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-197">Impact</span>
        </button>
      </div>
    </div>;
}
// Export as default for lazy()
export default {
  CollaborationPage
};